/**
 * Base API Client
 * Centralized HTTP client with typed requests and error handling
 */

import type { ApiError, ApiResult, RequestOptions } from "./types";

// ============================================================================
// Configuration
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_URL;

if (!API_BASE_URL) {
  console.warn(
    "[API] VITE_PUBLIC_API_URL is not set. API calls will fail.",
  );
}

console.log("[API] Using base URL:", API_BASE_URL);

const DEFAULT_TIMEOUT = 30000; // 30 seconds

// ============================================================================
// Error Handling
// ============================================================================

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiClientError";
  }

  toApiError(): ApiError {
    return {
      message: this.message,
      status: this.status,
      code: this.code,
      details: this.details,
    };
  }
}

async function parseErrorResponse(response: Response): Promise<ApiError> {
  try {
    const data = await response.json();
    return {
      message: data.message || data.error || `Request failed with status ${response.status}`,
      status: response.status,
      code: data.code,
      details: data.details,
    };
  } catch {
    return {
      message: `Request failed with status ${response.status}`,
      status: response.status,
    };
  }
}

// ============================================================================
// Authentication
// ============================================================================

export async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const { fetchAuthSession } = await import("aws-amplify/auth");
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    if (!idToken) throw new Error("No ID token available");
    return {
      Authorization: `Bearer ${idToken.toString()}`,
    };
  } catch (error) {
    console.error("Failed to get auth headers:", error);
    throw error;
  }
}

// ============================================================================
// HTTP Methods
// ============================================================================

async function request<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  endpoint: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<ApiResult<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  const { headers = {}, signal, timeout = DEFAULT_TIMEOUT } = options;

  // Create abort controller for timeout, and hook up any caller-provided abort signal.
  const controller = new AbortController();
  const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;

  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  const requestSignal = controller.signal;
  try {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: requestSignal,
    });

    if (!response.ok) {
      const error = await parseErrorResponse(response);
      return { success: false, error };
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return { success: true, data: undefined as T, status: response.status };
    }

    const data = await response.json();
    return { success: true, data: data as T, status: response.status };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      return {
        success: false,
        error: {
          message: "Request timeout",
          status: 408,
          code: "TIMEOUT",
        },
      };
    }

    return {
      success: false,
      error: {
        message: err instanceof Error ? err.message : "Network error",
        status: 0,
        code: "NETWORK_ERROR",
      },
    };
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

// ============================================================================
// Public API Client
// ============================================================================

export const apiClient = {
  /**
   * Perform a GET request
   * @param endpoint - API endpoint (e.g., "/users/profile")
   * @param options - Request options
   */
  get<T>(endpoint: string, options?: RequestOptions): Promise<ApiResult<T>> {
    return request<T>("GET", endpoint, undefined, options);
  },

  /**
   * Perform a POST request
   * @param endpoint - API endpoint (e.g., "/checkout/session")
   * @param body - Request body
   * @param options - Request options
   */
  post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<ApiResult<T>> {
    return request<T>("POST", endpoint, body, options);
  },

  /**
   * Perform a PUT request
   * @param endpoint - API endpoint
   * @param body - Request body
   * @param options - Request options
   */
  put<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<ApiResult<T>> {
    return request<T>("PUT", endpoint, body, options);
  },

  /**
   * Perform a PATCH request
   * @param endpoint - API endpoint
   * @param body - Request body
   * @param options - Request options
   */
  patch<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<ApiResult<T>> {
    return request<T>("PATCH", endpoint, body, options);
  },

  /**
   * Perform a DELETE request
   * @param endpoint - API endpoint
   * @param options - Request options
   */
  delete<T>(endpoint: string, options?: RequestOptions): Promise<ApiResult<T>> {
    return request<T>("DELETE", endpoint, undefined, options);
  },

  /**
   * Get the base URL for the API
   */
  getBaseUrl(): string {
    return API_BASE_URL;
  },
};

export default apiClient;
